import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MessageThread } from '@/components/support/MessageThread';
import { ReplyBox } from '@/components/support/ReplyBox';
import { SupportPriorityBadge, SupportStatusBadge } from '@/components/support/SupportBadges';
import {
  SUPPORT_PRIORITY_LABELS,
  SUPPORT_STATUS_LABELS,
  supportCategoryLabel,
} from '@/components/support/labels';
import {
  useAddSupportMessage,
  useDeleteSupportTicket,
  useSupportTicket,
  useUpdateSupportTicket,
} from '@/hooks/useSupport';
import { useEmployees } from '@/lib/hooks/useEmployees';
import { authStateAtom } from '@/stores/auth';
import { EmployeeRole } from '@repo/types';
import type { SupportTicketPriority, SupportTicketStatus } from '@repo/types';

export const Route = createFileRoute('/admin/support/$ticketId')({
  component: SupportTicketPage,
});

const STATUSES: SupportTicketStatus[] = [
  'open',
  'in_progress',
  'waiting_on_customer',
  'resolved',
  'closed',
];

const PRIORITIES: SupportTicketPriority[] = ['low', 'normal', 'high', 'urgent'];

/** Roles the API lets close a ticket or take one off another staff member. */
const MANAGE_ROLES: string[] = [EmployeeRole.Manager, EmployeeRole.Admin];

/**
 * Radix Select has no concept of an empty value, so "nobody" needs a real one.
 * Mapped back to `null` before it reaches the API.
 */
const UNASSIGNED = 'unassigned';

/** Short role names for the assignee picker, where space is tight. */
const ROLE_LABELS: Record<string, string> = {
  [EmployeeRole.Admin]: 'Admin',
  [EmployeeRole.Manager]: 'Manager',
  [EmployeeRole.TeamLead]: 'Team lead',
  [EmployeeRole.JobScout]: 'Job scout',
};

function stamp(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'd MMM yyyy, HH:mm');
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm">{value}</span>
    </div>
  );
}

function SupportTicketPage() {
  const { ticketId } = Route.useParams();
  const navigate = useNavigate();

  const user = useAtomValue(authStateAtom).user;
  const canManage = MANAGE_ROLES.includes(user?.role ?? '');

  const { data: ticket, isLoading, error } = useSupportTicket(ticketId);

  // Every staff role can work support, so every active employee is a valid
  // assignee. One page of 100 covers the team comfortably; if it ever does not,
  // this wants a searchable picker rather than a bigger number.
  const { data: employeePage } = useEmployees({ status: 'active' }, 1, 100);
  const assignees = useMemo(() => {
    // Yourself first: assigning to me is by far the most common choice, and it
    // saves scrolling a long team list to find your own name.
    const all = [...(employeePage?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const mine = all.filter((e) => e.id === user?.id);
    return [...mine, ...all.filter((e) => e.id !== user?.id)];
  }, [employeePage, user?.id]);
  const addMessage = useAddSupportMessage(ticketId);
  const update = useUpdateSupportTicket(ticketId);
  const remove = useDeleteSupportTicket();

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/support">
            <ArrowLeft className="size-4" />
            Support
          </Link>
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error?.message ?? 'Ticket not found.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const isClosed = ticket.status === 'closed';
  const assignedToMe = ticket.assignedTo === user?.id;
  // Matches the API's rule exactly: picking up an unassigned ticket, or letting
  // go of your own, is open to everyone; taking one off someone else is not.
  const canReassign = canManage || ticket.assignedTo === null || assignedToMe;
  // A deactivated assignee is absent from the active list; without this the
  // Select renders blank rather than showing who holds the ticket.
  const options =
    ticket.assignedTo && !assignees.some((e) => e.id === ticket.assignedTo)
      ? [{ id: ticket.assignedTo, name: ticket.assignedToName ?? 'Unknown', role: '' }, ...assignees]
      : assignees;

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/support">
            <ArrowLeft className="size-4" />
            Support
          </Link>
        </Button>

        {canManage && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="size-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {ticket.reference}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the ticket and every message on it, including the customer&apos;s
                  own. There is no undo. Closing it is almost always the right action instead.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() =>
                    remove.mutate(ticketId, {
                      onSuccess: () => navigate({ to: '/admin/support' }),
                    })
                  }
                >
                  Delete permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_290px]">
        <div className="min-w-0 space-y-4">
          <div className="space-y-1.5">
            <h1 className="text-xl font-semibold tracking-tight">{ticket.subject}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{ticket.reference}</span>
              <SupportStatusBadge status={ticket.status} />
              <SupportPriorityBadge priority={ticket.priority} />
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <MessageThread messages={ticket.messages} />
          </div>

          <ReplyBox
            onSend={(body, visibility) => addMessage.mutateAsync({ body, visibility })}
            sending={addMessage.isPending}
            disabled={isClosed}
            disabledReason="This ticket is closed. Reopen it to reply to the customer. Internal notes still work."
          />
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <h2 className="mb-2 text-sm font-medium">Customer</h2>
            <MetaRow label="Name" value={ticket.customer.name} />
            <MetaRow label="Email" value={ticket.customer.email ?? '—'} />
            <MetaRow label="Phone" value={ticket.customer.phone ?? '—'} />
            <Button variant="outline" size="sm" className="mt-2 w-full" asChild>
              <Link to="/admin/customers/$id" params={{ id: ticket.customer.id }}>
                Open customer
              </Link>
            </Button>
          </div>

          <div className="space-y-3 rounded-lg border bg-card p-4">
            <h2 className="text-sm font-medium">Ticket</h2>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="ticket-status">
                Status
              </label>
              <Select
                value={ticket.status}
                onValueChange={(value) => update.mutate({ status: value as SupportTicketStatus })}
              >
                <SelectTrigger id="ticket-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((value) => (
                    <SelectItem
                      key={value}
                      value={value}
                      // The API rejects it anyway; disabling here means staff
                      // find out before they try rather than through a toast.
                      disabled={!canManage && (value === 'closed' || isClosed)}
                    >
                      {SUPPORT_STATUS_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="ticket-priority">
                Priority
              </label>
              <Select
                value={ticket.priority}
                onValueChange={(value) =>
                  update.mutate({ priority: value as SupportTicketPriority })
                }
              >
                <SelectTrigger id="ticket-priority" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {SUPPORT_PRIORITY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs text-muted-foreground" htmlFor="ticket-assignee">
                Assignee
              </label>

              {canReassign ? (
                <>
                  <Select
                    value={ticket.assignedTo ?? UNASSIGNED}
                    onValueChange={(value) =>
                      update.mutate({ assignedTo: value === UNASSIGNED ? null : value })
                    }
                  >
                    <SelectTrigger id="ticket-assignee" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    {/* The shared SelectContent grows to the full available
                        viewport height, which is right for a five-item status
                        list and unusable for a whole team. Bounded here rather
                        than in the shared component so no other dropdown moves. */}
                    <SelectContent className="max-h-64">
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {options.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          <span className="flex items-baseline gap-1.5">
                            {employee.name}
                            {employee.id === user?.id && (
                              <span className="text-xs text-muted-foreground">(me)</span>
                            )}
                            {/* Names repeat on a real team; the role is what
                                tells two "Priya"s apart. */}
                            <span className="text-xs text-muted-foreground">
                              {ROLE_LABELS[employee.role] ?? employee.role}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Claiming it yourself is the common case, so it keeps a
                      one-click path rather than needing a trip through the list. */}
                  {!assignedToMe && user?.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-full text-xs"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ assignedTo: user.id })}
                    >
                      Assign to me
                    </Button>
                  )}
                </>
              ) : (
                // Mirrors the API rule exactly: taking a ticket off someone else
                // needs manage rights, so the control is read-only rather than
                // offering a change that would come back 403.
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm">
                    {ticket.assignedToName ?? 'Unassigned'}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Manager or admin only
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h2 className="mb-2 text-sm font-medium">Timeline</h2>
            <MetaRow label="Category" value={supportCategoryLabel(ticket.category)} />
            <MetaRow label="Opened" value={stamp(ticket.createdAt)} />
            <MetaRow label="First reply" value={stamp(ticket.firstRespondedAt)} />
            <MetaRow label="Last message" value={stamp(ticket.lastMessageAt)} />
            <MetaRow label="Customer last read" value={stamp(ticket.customerLastReadAt)} />
            {ticket.resolvedAt && <MetaRow label="Resolved" value={stamp(ticket.resolvedAt)} />}
            {ticket.closedAt && <MetaRow label="Closed" value={stamp(ticket.closedAt)} />}
          </div>
        </aside>
      </div>
    </div>
  );
}
